import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Tailwind-aware class joiner. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Percentage formatter for a value the API expresses in percentage POINTS.
 *
 * It takes `number | null` and returns `null` for null, so a caller cannot
 * accidentally render "0%" for a measurement that was never made — the caller
 * has to handle the null branch explicitly.
 */
export function formatPercent(value: number | null, digits = 0): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return `${value.toFixed(digits)}%`;
}

/** Thousands-separated integer. Null stays null; see <Metric>. */
export function formatCount(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat(undefined).format(value);
}

/** Clamps a percentage into 0..100 for bar widths only, never for display. */
export function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/** Turns a snake_case enum value into a human label ("not_collected" -> "Not collected"). */
export function humanise(value: string): string {
  if (value === "") return "";
  const spaced = value.replace(/[_.]/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
